/**
 * Bridge from timeline spans to raw Event[] for the transcript display pipeline.
 *
 * Resolves the selected swimlane row to TimelineSpan(s), then walks their
 * content trees to produce a flat Event[] that can be fed through useEventNodes.
 * Child TimelineSpans are re-emitted as synthetic span_begin/span_end events
 * so treeifyEvents can reconstruct the hierarchy.
 */

import type {
  Event,
  SpanBeginEvent,
  SpanEndEvent,
} from "@tsmono/inspect-common/types";
import { isRecord } from "@tsmono/util";

import { TYPE_CHECKPOINT } from "../transform/utils";
import { EventNode } from "../types";

import {
  createBranchSpan,
  isEmptyBranch,
  stripSuffix,
  type TimelineEvent,
  type TimelineSpan,
} from "./core";
import { getAgents, isSingleSpan, type SwimlaneRow } from "./swimlaneRows";

/** Selection region for the minimap overlay. */
export interface MinimapSelection {
  startTime: Date;
  endTime: Date;
  totalTokens: number;
}

// =============================================================================
// Selection parsing
// =============================================================================

/**
 * Parsed selection: a row key with optional span index and region index.
 *
 * Format: `"rowKey"` selects the whole row, `"rowKey:N"` selects
 * span index N (0-based) within an iterative row, `"rowKey@R"` selects
 * compaction region R, and `"rowKey:N@R"` selects region R of span N.
 */
export interface ParsedSelection {
  rowKey: string;
  spanIndex: number | null;
  regionIndex: number | null;
}

/**
 * Parses a selection string into row key + optional span index + optional region index.
 * Returns null for null/empty input.
 *
 * Parse order: extract `@R` from end first, then `:N` from what remains.
 */
export function parseSelection(
  selected: string | null
): ParsedSelection | null {
  if (!selected) return null;

  // Extract @R region suffix first
  let regionIndex: number | null = null;
  let rest = selected;
  const atIdx = rest.lastIndexOf("@");
  if (atIdx !== -1) {
    const regionSuffix = rest.slice(atIdx + 1);
    const r = Number(regionSuffix);
    if (Number.isInteger(r) && r >= 0) {
      regionIndex = r;
      rest = rest.slice(0, atIdx);
    }
  }

  // Extract :N span index from the remainder
  const colonIdx = rest.lastIndexOf(":");
  if (colonIdx === -1) {
    return { rowKey: rest, spanIndex: null, regionIndex };
  }
  const suffix = rest.slice(colonIdx + 1);
  const idx = Number(suffix);
  if (!Number.isInteger(idx) || idx < 0) {
    // Not a valid span index — treat the whole remaining string as the row key
    return { rowKey: rest, spanIndex: null, regionIndex };
  }
  return { rowKey: rest.slice(0, colonIdx), spanIndex: idx, regionIndex };
}

/**
 * Builds a selection string from row key + optional span index + optional region index.
 */
export function buildSelectionKey(
  rowKey: string,
  spanIndex?: number,
  regionIndex?: number
): string {
  let key = rowKey;
  if (spanIndex !== undefined) key = `${key}:${spanIndex}`;
  if (regionIndex !== undefined) key = `${key}@${regionIndex}`;
  return key;
}

// =============================================================================
// Row lookup
// =============================================================================

/** Find a swimlane row by key. */
function findRowByKey(
  rows: SwimlaneRow[],
  key: string
): SwimlaneRow | undefined {
  return rows.find((r) => r.key === key);
}

/**
 * Derive the branch prefix for nested branches from a selected row.
 *
 * If the row is a branch named "Branch 1", returns "1." so nested branches
 * become "Branch 1.1", "Branch 1.2", etc. Returns "" for non-branch rows.
 */
export function getBranchPrefix(
  rows: SwimlaneRow[],
  selected: string | null
): string {
  const parsed = parseSelection(selected);
  if (!parsed) return "";
  const row = findRowByKey(rows, parsed.rowKey);
  if (!row?.branch) return "";
  const match = /^Branch (\S+)$/i.exec(row.name);
  return match ? `${match[1]}.` : "";
}

// =============================================================================
// Selected spans
// =============================================================================

/**
 * Resolves the selected swimlane row key to TimelineSpan(s).
 *
 * When the selection includes a span index (e.g. `"explore:1"`), returns
 * only that specific span. Otherwise returns all spans from the row.
 */
export function getSelectedSpans(
  rows: SwimlaneRow[],
  selected: string | null
): TimelineSpan[] {
  const parsed = parseSelection(selected);
  if (!parsed) return [];

  const row = findRowByKey(rows, parsed.rowKey);
  if (!row) return [];

  // Sub-selection: return only the indexed span
  if (parsed.spanIndex !== null) {
    const span = row.spans[parsed.spanIndex];
    if (!span) return [];
    return isSingleSpan(span) ? [span.agent] : getAgents(span);
  }

  // Whole row: return all spans
  const result: TimelineSpan[] = [];
  for (const rowSpan of row.spans) {
    if (isSingleSpan(rowSpan)) {
      result.push(rowSpan.agent);
    } else {
      result.push(...getAgents(rowSpan));
    }
  }
  return result;
}

// =============================================================================
// Minimap selection
// =============================================================================

/**
 * Computes the minimap selection for the currently selected swimlane row.
 *
 * When a span index is present, shows just that span's range.
 * When a region index is present, narrows to the region's time range.
 * Otherwise shows the full row's range.
 */
export function computeMinimapSelection(
  rows: SwimlaneRow[],
  selected: string | null
): MinimapSelection | undefined {
  const parsed = parseSelection(selected);
  if (!parsed) return undefined;
  const row = findRowByKey(rows, parsed.rowKey);
  if (!row) return undefined;

  // Determine which spans to include
  const spans =
    parsed.spanIndex !== null
      ? row.spans[parsed.spanIndex]
        ? [row.spans[parsed.spanIndex]!]
        : []
      : row.spans;

  const allAgents = spans.flatMap(getAgents);
  if (allAgents.length === 0) return undefined;

  // Region-scoped minimap: narrow to the region's time range
  if (parsed.regionIndex !== null && allAgents.length === 1) {
    const agent = allAgents[0]!;
    const regions = computeCompactionRegions(agent.content);
    const region = regions[parsed.regionIndex];
    if (region && region.length > 0) {
      const times = region.flatMap((item) =>
        item.type === "event"
          ? [item.startTime(), item.endTime()]
          : [item.startTime(), item.endTime()]
      );
      const startTime = new Date(Math.min(...times.map((t) => t.getTime())));
      const endTime = new Date(Math.max(...times.map((t) => t.getTime())));
      const tokens = region.reduce((sum, item) => sum + item.totalTokens(), 0);
      return { startTime, endTime, totalTokens: tokens };
    }
  }

  if (allAgents.length === 1) {
    const agent = allAgents[0]!;
    return {
      startTime: agent.startTime(false),
      endTime: agent.endTime(false),
      totalTokens: agent.totalTokens(false),
    };
  }

  // Compute envelope with includeBranches=false so each agent contributes
  // only its own time range
  let envStart = allAgents[0]!.startTime(false);
  let envEnd = allAgents[0]!.endTime(false);
  for (let i = 1; i < allAgents.length; i++) {
    const a = allAgents[i]!;
    if (a.startTime(false) < envStart) envStart = a.startTime(false);
    if (a.endTime(false) > envEnd) envEnd = a.endTime(false);
  }
  const tokens = allAgents.reduce((sum, a) => sum + a.totalTokens(false), 0);
  return { startTime: envStart, endTime: envEnd, totalTokens: tokens };
}

// =============================================================================
// Compaction region computation
// =============================================================================

/**
 * Partitions a content array into regions separated by compaction events.
 *
 * Returns an array of content slices. If no compaction events exist, returns
 * a single-element array containing the full content.
 */
export function computeCompactionRegions(
  content: ReadonlyArray<TimelineEvent | TimelineSpan>
): ReadonlyArray<TimelineEvent | TimelineSpan>[] {
  const regions: (TimelineEvent | TimelineSpan)[][] = [];
  let current: (TimelineEvent | TimelineSpan)[] = [];

  for (const item of content) {
    if (item.type === "event" && item.event.event === "compaction") {
      regions.push(current);
      current = [];
    } else {
      current.push(item);
    }
  }
  regions.push(current);
  return regions;
}

// =============================================================================
// Collected events
// =============================================================================

export interface CollectedEvents {
  events: Event[];
  /** Agent spans keyed by span ID, for attaching to EventNodes after tree construction. */
  sourceSpans: Map<string, TimelineSpan>;
}

/**
 * Collects raw Event[] from TimelineSpan content trees.
 *
 * When a single span is provided, walks its content directly (the span itself
 * is the implicit context). When multiple spans are provided, each is wrapped
 * in synthetic span_begin/span_end events so treeifyEvents can reconstruct
 * the grouping (e.g. parallel agents shown as collapsible sections).
 *
 * Agent spans (spanType === "agent") are emitted as empty span_begin/span_end
 * pairs with no child events — their content is accessed by selecting the
 * swimlane row. The returned sourceSpans map allows attaching the original
 * TimelineSpan to the resulting EventNodes for rich rendering.
 *
 * When `regionIndex` is set, only events from that compaction region are emitted.
 */
export function collectRawEvents(
  spans: TimelineSpan[],
  options?: {
    includeUtility?: boolean;
    regionIndex?: number | null;
    showBranches?: boolean;
    branchPrefix?: string;
  }
): CollectedEvents {
  const includeUtility = options?.includeUtility ?? false;
  const regionIndex = options?.regionIndex ?? null;
  const showBranches = options?.showBranches ?? false;
  const branchPrefix = options?.branchPrefix ?? "";
  const events: Event[] = [];
  const sourceSpans = new Map<string, TimelineSpan>();
  if (spans.length === 1) {
    // When viewing a single agent span, the spawning ToolEvent (which wraps
    // the entire agent execution) duplicates the first MODEL CALL's input and
    // the last ASSISTANT response. Detect and skip it at the top level only.
    const span = spans[0]!;
    const agentSpanId = span.spanType === "agent" ? span.id : undefined;

    // If a region is selected, window the content to that region
    let content: ReadonlyArray<TimelineEvent | TimelineSpan> = span.content;
    if (regionIndex !== null) {
      const regions = computeCompactionRegions(span.content);
      if (regionIndex >= 0 && regionIndex < regions.length) {
        content = regions[regionIndex]!;
      }
    }

    collectFromContent(
      content,
      events,
      sourceSpans,
      agentSpanId,
      includeUtility,
      showBranches,
      span.branches.length > 0 ? span.branches : undefined,
      branchPrefix,
      false
    );
  } else {
    // Multiple spans: wrap each in span_begin/span_end so the event tree
    // groups them, matching the drilled-in container behavior.
    // Region selection does not apply to multi-span views.
    collectFromContent(
      spans,
      events,
      sourceSpans,
      undefined,
      includeUtility,
      showBranches,
      undefined,
      branchPrefix
    );
  }
  return { events, sourceSpans };
}

/**
 * Emit a single branch as an empty span_begin/span_end pair (like agents).
 * Content is accessed by selecting the branch swimlane row.
 */
function emitBranchSpan(
  branch: TimelineSpan,
  label: string,
  out: Event[],
  sourceSpans: Map<string, TimelineSpan>,
  parentSpanId?: string | null
): void {
  const branchSpan = createBranchSpan(branch, label);
  sourceSpans.set(branchSpan.id, branchSpan);

  const branchBegin: SpanBeginEvent = {
    event: "span_begin",
    name: branchSpan.name,
    id: branchSpan.id,
    span_id: branchSpan.id,
    type: branchSpan.spanType,
    timestamp: branchSpan.startTime().toISOString(),
    parent_id: parentSpanId ?? null,
    pending: false,
    working_start: 0,
    uuid: branchSpan.id,
    metadata: null,
  };
  out.push(branchBegin);

  const branchEnd: SpanEndEvent = {
    event: "span_end",
    id: `${branchSpan.id}-end`,
    span_id: branchSpan.id,
    timestamp: branchSpan.endTime().toISOString(),
    pending: false,
    working_start: 0,
    uuid: null,
    metadata: null,
  };
  out.push(branchEnd);
}

function collectFromContent(
  content: ReadonlyArray<TimelineEvent | TimelineSpan>,
  out: Event[],
  sourceSpans: Map<string, TimelineSpan>,
  skipAgentSpanId?: string,
  includeUtility: boolean = false,
  showBranches: boolean = false,
  branches?: ReadonlyArray<TimelineSpan>,
  branchPrefix: string = "",
  isRoot: boolean = true
): void {
  // When there's exactly one agent span at the root of the selected view,
  // inline its events instead of emitting an empty agent card. This avoids
  // a redundant wrapper when the root view has a single agent plus scoring.
  // Only applies at the root — nested agents (e.g. a subagent spawned by a
  // task tool) should still render as navigable cards.
  const agentSpans = isRoot
    ? content.filter(
        (item): item is TimelineSpan =>
          item.type === "span" && item.spanType === "agent"
      )
    : [];
  const inlineAgentId = agentSpans.length === 1 ? agentSpans[0]?.id : undefined;

  // Track agent tool_call_ids whose results are shown on the AgentCard,
  // so we can filter them from the next model event's input.
  const pendingToolCallIds = new Set<string>();

  // Build a lookup from branchedFrom UUID → branches for inline emission.
  // Branches whose branchedFrom doesn't match any event are emitted at the end.
  const branchByBranchedFrom = new Map<string, TimelineSpan[]>();
  if (branches) {
    for (const branch of branches) {
      if (branch.branchedFrom) {
        const existing = branchByBranchedFrom.get(branch.branchedFrom);
        if (existing) {
          existing.push(branch);
        } else {
          branchByBranchedFrom.set(branch.branchedFrom, [branch]);
        }
      }
    }
  }
  const emittedBranchedFroms = new Set<string>();

  // Checkpoint spans arrive unrolled as raw span_begin/span_end events and
  // the turns inside them nest under the checkpoint node. Child spans must
  // nest there too, or discard_checkpoint_span leaves them after every turn.
  const openCheckpointIds: string[] = [];

  for (const item of content) {
    if (item.type === "event") {
      if (
        item.event.event === "span_begin" &&
        item.event.type === TYPE_CHECKPOINT
      ) {
        openCheckpointIds.push(item.event.id);
      } else if (item.event.event === "span_end") {
        const index = openCheckpointIds.lastIndexOf(item.event.id);
        if (index !== -1) openCheckpointIds.splice(index, 1);
      }

      // Skip the spawning ToolEvent when viewing a sub-agent's own content.
      // Match the specific agent_span_id so we only skip the tool that
      // spawned this exact agent, not any other agent-spawning tool.
      if (
        skipAgentSpanId &&
        item.event.event === "tool" &&
        item.event.agent_span_id === skipAgentSpanId
      ) {
        continue;
      }

      // Filter agent tool results from model event inputs — these are
      // already shown on the AgentCard, so don't duplicate them inline.
      if (item.event.event === "model" && pendingToolCallIds.size > 0) {
        const modelEvent = item.event;
        const filteredInput = (
          modelEvent.input as Array<Record<string, unknown>>
        ).filter(
          (msg) =>
            !(
              msg.role === "tool" &&
              typeof msg.tool_call_id === "string" &&
              pendingToolCallIds.has(msg.tool_call_id)
            )
        );
        if (filteredInput.length !== modelEvent.input.length) {
          // Mark the event so ModelEventView knows agent tool results were
          // filtered and it should not crawl backward through input messages.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- marker field: `agentResultsFiltered` is a view-layer flag ModelEventView reads, not part of the generated ModelEvent
          const patched = {
            ...modelEvent,
            input: filteredInput,
            agentResultsFiltered: true,
          } as unknown as Event;
          out.push(patched);
          pendingToolCallIds.clear();
          // Emit branches forked at this event after the event itself
          emitInlineBranches(
            item,
            branchByBranchedFrom,
            branches ?? [],
            emittedBranchedFroms,
            out,
            sourceSpans,
            branchPrefix
          );
          continue;
        }
      }

      out.push(item.event);

      // Emit branches forked at this event immediately after it
      emitInlineBranches(
        item,
        branchByBranchedFrom,
        branches ?? [],
        emittedBranchedFroms,
        out,
        sourceSpans,
        branchPrefix
      );
    } else if (!includeUtility && item.utility) {
      // Elide utility spans — internal model calls (e.g. file path
      // extraction). The swimlane header shows a hidden-utility count so
      // these never disappear without a trace.
      continue;
    } else {
      // Emit synthetic span_begin
      const beginEvent: SpanBeginEvent = {
        event: "span_begin",
        name: item.name,
        id: item.id,
        span_id: item.id,
        type: item.spanType,
        timestamp: item.startTime().toISOString(),
        parent_id: openCheckpointIds.at(-1) ?? null,
        pending: false,
        working_start: 0,
        uuid: item.id,
        metadata: null,
      };
      out.push(beginEvent);

      if (item.spanType === "agent" && item.id !== inlineAgentId) {
        // Agent spans: emit empty begin/end pair. Content is accessed
        // by selecting the swimlane row, not by expanding in-place.
        sourceSpans.set(item.id, item);
        // Track agent tool_call_ids with results for filtering from next model event
        if (item.agentResult && item.id.startsWith("agent-")) {
          pendingToolCallIds.add(item.id.slice(6));
        }
      } else {
        // Non-agent spans: recurse into child content, passing their
        // own branches so they emit inline at the correct fork point.
        collectFromContent(
          item.content,
          out,
          sourceSpans,
          undefined,
          includeUtility,
          showBranches,
          item.branches.length > 0 ? item.branches : undefined,
          "",
          false
        );
      }

      // Emit synthetic span_end
      const endEvent: SpanEndEvent = {
        event: "span_end",
        id: `${item.id}-end`,
        span_id: item.id,
        timestamp: item.endTime().toISOString(),
        pending: false,
        working_start: 0,
        uuid: null,
        metadata: null,
      };
      out.push(endEvent);
    }
  }

  // Emit any branches whose branchedFrom didn't match any event in this content
  // (fallback to end, preserving previous behavior for unresolvable forks).
  if (branches) {
    for (const branch of branches) {
      const branchedFrom = branch.branchedFrom ?? "";
      if (!emittedBranchedFroms.has(branchedFrom)) {
        const label = `${branchPrefix}${branches.indexOf(branch) + 1}`;
        emitBranchSpan(branch, label, out, sourceSpans);
      }
    }
  }
}

/**
 * Emit branches inline after their fork point event.
 */
function emitInlineBranches(
  item: TimelineEvent,
  branchByBranchedFrom: ReadonlyMap<string, TimelineSpan[]>,
  allBranches: ReadonlyArray<TimelineSpan>,
  emittedBranchedFroms: Set<string>,
  out: Event[],
  sourceSpans: Map<string, TimelineSpan>,
  branchPrefix: string = ""
): void {
  // branchByBranchedFrom is keyed by message ID. Find entries where
  // this event produced or carries the matching message ID.
  for (const [messageId, forkedBranches] of branchByBranchedFrom) {
    if (emittedBranchedFroms.has(messageId)) continue;
    if (!item.matchesForkPoint(messageId)) continue;

    // Use the fork event's span_id as the branch's parent so treeifyEvents
    // nests the branch inside the same span as the fork event.
    const parentSpanId = (item.event as { span_id?: string | null }).span_id;
    for (const branch of forkedBranches) {
      // Use the branch's position in the full branches array for the display label
      const globalIndex = allBranches.indexOf(branch);
      const label = `${branchPrefix}${(globalIndex >= 0 ? globalIndex : 0) + 1}`;
      emitBranchSpan(branch, label, out, sourceSpans, parentSpanId);
    }
    emittedBranchedFroms.add(messageId);
  }
}

// =============================================================================
// Branch context collection
// =============================================================================

/**
 * Derives the parent row key from a branch row key.
 *
 * Branch keys follow the pattern `"parentKey/branch-{branchedFrom}-{index}"`.
 * Returns null if the key doesn't contain a branch segment.
 */
export function getParentKeyFromBranch(branchKey: string): string | null {
  const match = /^(.+)\/branch-[^/]+$/.exec(branchKey);
  return match ? match[1]! : null;
}

// =============================================================================
// Fork-navigator path collection
// =============================================================================

/** One selectable continuation at a fork point. */
export interface ForkNavOption {
  label: string;
  rowKey: string;
}

/** One group within a fork navigator — the options forked at a single anchor. */
export interface ForkNavGroup {
  anchorId: string;
  options: ForkNavOption[];
  selectedIndex: number;
}

export interface ForkNavData {
  groups: ForkNavGroup[];
}

/**
 * Shallow shape check for fork_nav metadata read off an event. Deliberately
 * only checks the outer shape — `forkNavToBranchPointProps` returns null for
 * groups it can't use, so a deep walk here would duplicate that.
 */
export const isForkNavData = (value: unknown): value is ForkNavData =>
  isRecord(value) && Array.isArray(value["groups"]);

export interface EmptyBranchData {
  /** Branch (trajectory) name, for display. */
  branchName: string;
  /**
   * Function name of the last tool event observed inside the trajectory's
   * span range. Null when no tool was found or no events array was given.
   */
  terminator: string | null;
}

interface PathSegment {
  span: TimelineSpan;
  rowKey: string;
  /** Anchor at which this segment hands off to the next; null for the leaf. */
  cutAnchor: string | null;
}

function rowSpan(row: SwimlaneRow | undefined): TimelineSpan | null {
  const s = row?.spans[0];
  if (!s) return null;
  return isSingleSpan(s) ? s.agent : (getAgents(s)[0] ?? null);
}

/** Build root→selected segment list, each carrying its row key and cut anchor. */
function buildPath(rows: SwimlaneRow[], selectedKey: string): PathSegment[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const keys: string[] = [selectedKey];
  let k = selectedKey;
  while (true) {
    const parent = getParentKeyFromBranch(k);
    if (!parent || !byKey.has(parent)) break;
    keys.unshift(parent);
    k = parent;
  }
  const segs: PathSegment[] = [];
  for (let i = 0; i < keys.length; i++) {
    const span = rowSpan(byKey.get(keys[i]!));
    if (!span) return segs;
    const next = rowSpan(byKey.get(keys[i + 1] ?? ""));
    segs.push({
      span,
      rowKey: keys[i]!,
      cutAnchor: next?.branchedFrom ?? null,
    });
  }
  return segs;
}

/** Group a span's branches by the anchor they fork from, preserving `.branches` order. */
function branchesByAnchor(
  span: TimelineSpan,
  parentRowKey: string
): Map<string, ForkNavOption[]> {
  const map = new Map<string, ForkNavOption[]>();
  span.branches.forEach((b, i) => {
    const anchor = b.branchedFrom ?? "";
    const opt: ForkNavOption = {
      label: b.name,
      rowKey: `${parentRowKey}/branch-${b.branchedFrom}-${i + 1}`,
    };
    const arr = map.get(anchor);
    if (arr) arr.push(opt);
    else map.set(anchor, [opt]);
  });
  return map;
}

interface SyntheticSpanOpts {
  id: string;
  name: string;
  type: string | null;
  parentId: string | null;
  start: string;
  end?: string;
  metadata?: Record<string, unknown> | null;
}

/** Push a synthetic span_begin/span_end pair onto `events`. */
function emitSyntheticSpan(events: Event[], opts: SyntheticSpanOpts): void {
  events.push({
    event: "span_begin",
    name: opts.name,
    id: opts.id,
    span_id: opts.id,
    type: opts.type,
    timestamp: opts.start,
    parent_id: opts.parentId,
    pending: false,
    working_start: 0,
    uuid: opts.id,
    metadata: opts.metadata ?? null,
  } satisfies SpanBeginEvent);
  events.push({
    event: "span_end",
    id: opts.id,
    span_id: opts.id,
    timestamp: opts.end ?? opts.start,
    pending: false,
    working_start: 0,
    uuid: null,
    metadata: null,
  } satisfies SpanEndEvent);
}

/**
 * Outline label for a fork navigator. Flattens all groups' child options
 * (excluding the leading stay-on-segment entry, which is always groups[*].options[0])
 * and caps at "first, second +N".
 */
function forkNavLabel(groups: ForkNavGroup[]): string {
  const children = groups.flatMap((g) => g.options.slice(1));
  if (children.length <= 2) return children.map((c) => c.label).join(", ");
  return `${children[0]!.label} +${children.length - 1}`;
}

/**
 * Collect events for the root→selected path, emitting an inline fork navigator
 * at every anchor where one or more branches fork off.
 *
 * The navigator's options are `[stay-on-segment, child₁, child₂, …]` for
 * children sharing that anchor. `selectedIndex` is whichever option the
 * current path follows next.
 */
export function collectPathWithNavigators(
  rows: SwimlaneRow[],
  selectedRowKey: string,
  rawEvents?: ReadonlyArray<Event>
): CollectedEvents {
  const events: Event[] = [];
  const sourceSpans = new Map<string, TimelineSpan>();
  const path = buildPath(rows, selectedRowKey);
  // Track which segment emitted the most recent fork-nav so navs from
  // different segments never collapse — even if their parent_ids happen to
  // coincide (e.g., both null when a cut anchor has a null span_id and the
  // child segment opens with a restart-style fork).
  let lastNavSegIdx: number | null = null;

  for (let segIdx = 0; segIdx < path.length; segIdx++) {
    const seg = path[segIdx]!;
    const nextRowKey = path[segIdx + 1]?.rowKey;
    const forks = branchesByAnchor(seg.span, seg.rowKey);
    const suffix = `:${seg.span.id}`;
    sourceSpans.set(seg.span.id, seg.span);

    /** Emit a navigator for `children` at `anchorId`; returns true if the path cuts here. */
    const navAt = (
      anchorId: string,
      children: ForkNavOption[],
      ts: string,
      parentId: string | null
    ): boolean => {
      const options = [
        { label: seg.span.name, rowKey: seg.rowKey },
        ...children,
      ];
      const isCut = anchorId === seg.cutAnchor;
      const sel = isCut ? options.findIndex((o) => o.rowKey === nextRowKey) : 0;
      const group: ForkNavGroup = {
        anchorId,
        options,
        selectedIndex: Math.max(0, sel),
      };

      // Collapse strictly-adjacent fork navs (same parent_id) into one row.
      // Anchors are structural markers (hidden by default) — the user
      // perceives two fork navs separated only by anchors as adjacent — so
      // skip them when checking adjacency.
      let i = events.length - 1;
      while (i >= 0 && events[i]!.event === "anchor") i--;
      const last = events[i];
      const prev = events[i - 1];
      if (
        lastNavSegIdx === segIdx &&
        last &&
        prev &&
        last.event === "span_end" &&
        prev.event === "span_begin" &&
        prev.type === "fork_nav" &&
        last.span_id === prev.span_id &&
        prev.parent_id === parentId
      ) {
        const data = (
          prev.metadata as { fork_nav?: ForkNavData } | null | undefined
        )?.fork_nav;
        if (data) {
          data.groups.push(group);
          prev.name = forkNavLabel(data.groups);
          return isCut;
        }
      }

      emitSyntheticSpan(events, {
        id: `forknav-${seg.span.id}-${anchorId || "restart"}`,
        name: forkNavLabel([group]),
        type: "fork_nav",
        parentId,
        start: ts,
        metadata: {
          fork_nav: { groups: [group] } satisfies ForkNavData,
        },
      });
      lastNavSegIdx = segIdx;
      return isCut;
    };

    // Restart-style branches (branchedFrom === "") fork before any anchor.
    const restartForks = forks.get("");
    if (restartForks) {
      const ts = seg.span.startTime().toISOString();
      if (navAt("", restartForks, ts, null)) continue;
    }
    for (const item of seg.span.content) {
      if (item.type === "span") {
        sourceSpans.set(item.id, item);
        emitSyntheticSpan(events, {
          id: item.id,
          name: item.name,
          type: item.spanType,
          parentId: null,
          start: item.startTime().toISOString(),
          end: item.endTime().toISOString(),
        });
        continue;
      }
      const stripped = stripSuffix(item.event, suffix, seg.span.id);
      events.push(stripped);
      if (item.event.event !== "anchor") continue;
      const anchorId = item.event.anchor_id;
      const children = forks.get(anchorId);
      if (children?.length) {
        const ts = item.event.timestamp;
        if (navAt(anchorId, children, ts, stripped.span_id ?? null)) break;
      } else if (anchorId === seg.cutAnchor) {
        break;
      }
    }
  }

  // Defensive: empty branches are pruned at convert time, but a stale URL
  // can still target one. Surface an explanation in that case.
  const leaf = path[path.length - 1];
  if (leaf && leaf.span.spanType === "branch") {
    if (isEmptyBranch(leaf.span)) {
      const terminator = rawEvents
        ? findTerminatorTool(rawEvents, leaf.span.id)
        : null;
      const startTs = leaf.span.startTime().toISOString();
      emitSyntheticSpan(events, {
        id: `emptybranch-${leaf.span.id}`,
        name: `${leaf.span.name} (empty)`,
        type: "empty_branch",
        parentId: null,
        start: startTs,
        metadata: {
          empty_branch: {
            branchName: leaf.span.name,
            terminator,
          } satisfies EmptyBranchData,
        },
      });
    }
  }

  return { events, sourceSpans };
}

// =============================================================================
// Span select key lookup
// =============================================================================

export interface SpanSelectKey {
  /** The row key to select. */
  key: string;
}

/**
 * Builds a lookup from span ID to the selection key needed to select that span
 * in the swimlane UI.
 *
 * For rows with multiple spans (iterative), each span ID maps to a key with
 * the span index suffix (e.g. `"explore:0"`). For single-span rows, the key
 * is just the row key.
 */
export function buildSpanSelectKeys(
  rows: SwimlaneRow[]
): ReadonlyMap<string, SpanSelectKey> {
  const keys = new Map<string, SpanSelectKey>();
  for (const row of rows) {
    const hasMultipleSpans = row.spans.length > 1;
    for (let i = 0; i < row.spans.length; i++) {
      const rowSpan = row.spans[i]!;
      const selectKey = hasMultipleSpans
        ? buildSelectionKey(row.key, i)
        : row.key;
      if (isSingleSpan(rowSpan)) {
        keys.set(rowSpan.agent.id, { key: selectKey });
      } else {
        for (const agent of getAgents(rowSpan)) {
          keys.set(agent.id, { key: selectKey });
        }
      }
    }
  }
  return keys;
}

// =============================================================================
// Source span attachment
// =============================================================================

/**
 * Find the most recent tool event whose position sits inside the given
 * trajectory's span_begin/span_end range. Returns the tool's function name,
 * or null when no tool is found in range.
 */
export function findTerminatorTool(
  events: ReadonlyArray<Event>,
  trajectorySpanId: string
): string | null {
  let begin = -1;
  let end = -1;
  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    if (e.span_id !== trajectorySpanId) continue;
    if (e.event === "span_begin" && begin === -1) begin = i;
    else if (e.event === "span_end") end = i;
  }
  if (begin === -1 || end === -1) return null;
  for (let i = end - 1; i > begin; i--) {
    const e = events[i]!;
    if (e.event === "tool") return e.function;
  }
  return null;
}

/**
 * Walks the EventNode tree and attaches sourceSpan to any span_begin node
 * whose span_id matches an entry in the map. This links synthetic span events
 * back to their original TimelineSpan for rich rendering.
 */
export function attachSourceSpans(
  nodes: EventNode[],
  spanMap: ReadonlyMap<string, TimelineSpan>
): void {
  for (const node of nodes) {
    if (node.event.event === "span_begin") {
      const spanId = node.event.span_id;
      if (spanId) {
        const span = spanMap.get(spanId);
        if (span) node.sourceSpan = span;
      }
    }
    if (node.children.length > 0) {
      attachSourceSpans(node.children, spanMap);
    }
  }
}
